import React, { useCallback, useEffect, useRef } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { Image } from '@/atoms/Image'
import { CaptionMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import {
  EndOfAssetRailItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { RecommendedUpNextContent } from '@/services/RecommendationService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'

interface EndOfAssetRecommendationProps {
  recommendation: NonNullable<RecommendedUpNextContent[number]>
  handleRecommendationClick: (projectSlug: Maybe<string>) => void
  itemIndex?: number
}

export const EndOfAssetRecommendation: React.FC<EndOfAssetRecommendationProps> = ({
  recommendation,
  handleRecommendationClick,
  itemIndex = 0,
}) => {
  const { trackInteraction } = useContentInteraction()
  const [cardRef, isVisible] = useIntersectionObserver<HTMLAnchorElement>({
    threshold: 0.9, // Track when at least 90% of the card is visible
  })

  // Hover tracking state
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const onClick = useCallback(() => {
    if (!recommendation) return
    handleRecommendationClick(recommendation?.title?.project?.slug)

    // Skip interaction tracking if required data is missing
    if (!recommendation.title?.id || !recommendation.title?.image?.cloudinaryPath) return

    const interaction = new EndOfAssetRailItemInteraction({
      id: recommendation.title.id,
      __typename: recommendation.title.__typename ?? null,
      attribution: recommendation.attribution,
    })
      .withSurface('player')
      .withPlacement('up_next', 0, itemIndex, 'horizontal_swimlane')
      .withImageArtwork(recommendation.title.image.cloudinaryPath, 'landscape_display')
      .withPress()

    trackInteraction(interaction)
  }, [handleRecommendationClick, recommendation, trackInteraction, itemIndex])

  const handleMouseEnter = useCallback(() => {
    // Record the start time
    hoverStartTimeRef.current = Date.now()
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (!hoverStartTimeRef.current) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    // Only track hover if the user hovered for at least the threshold time
    if (dwellTime >= HOVER_THRESHOLD_MS) {
      // Skip interaction tracking if required data is missing
      if (recommendation.title?.id && recommendation.title?.image?.cloudinaryPath) {
        const interaction = new EndOfAssetRailItemInteraction({
          id: recommendation.title.id,
          __typename: recommendation.title.__typename ?? null,
          attribution: recommendation.attribution,
        })
          .withSurface('player')
          .withPlacement('up_next', 0, itemIndex, 'horizontal_swimlane')
          .withImageArtwork(recommendation.title.image.cloudinaryPath, 'landscape_display')
          .withHover(dwellTime)

        trackInteraction(interaction)
      }
    }

    // Reset state
    hoverStartTimeRef.current = null
  }, [recommendation, trackInteraction, itemIndex])

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (isVisible) {
      // Prevent duplicate timers
      if (impressionTimeoutRef.current || hasTrackedImpression.current) {
        return
      }

      // Start a timer to track impression after 1 second
      impressionTimeoutRef.current = setTimeout(() => {
        if (hasTrackedImpression.current) return
        hasTrackedImpression.current = true

        // Skip interaction tracking if required data is missing
        if (!recommendation.title?.id || !recommendation.title?.image?.cloudinaryPath) {
          impressionTimeoutRef.current = null
          return
        }

        const interaction = new EndOfAssetRailItemInteraction({
          id: recommendation.title.id,
          __typename: recommendation.title.__typename ?? null,
          attribution: recommendation.attribution,
        })
          .withSurface('player')
          .withPlacement('up_next', 0, itemIndex, 'horizontal_swimlane')
          .withImageArtwork(recommendation.title.image.cloudinaryPath, 'landscape_display')
          .withImpression()

        trackInteraction(interaction)
        impressionTimeoutRef.current = null
      }, 1000) // Wait 1 second before tracking impression
    } else {
      // Clear timeout if item becomes not visible before 1 second
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, recommendation, trackInteraction, itemIndex])

  const path = recommendation?.title?.image?.cloudinaryPath

  return (
    <a
      ref={cardRef}
      aria-label={recommendation.title?.name ?? ''}
      key={recommendation?.title?.id}
      href={`${paths.watch.index}/${recommendation?.title?.project?.slug}`}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative inline-block h-fit pb-2 pr-4 duration-[400ms] ease-in-out hover:z-10 md:hover:scale-[1.05]">
        {path ? (
          <Image
            aria-label={recommendation?.title?.name || undefined}
            alt={recommendation?.title?.name || undefined}
            src={getCloudinaryImageUrl({ path, width: 380 })}
            height={253}
            width={450}
            className="rounded-xl bg-black"
          />
        ) : (
          <CaptionMD>{recommendation?.title?.name}</CaptionMD>
        )}
      </div>
    </a>
  )
}
